import React, { useEffect, useState, useRef } from "react";
import { ProgrammingLanguage, Question } from "../../Interface/Index";
import Settings from "./Settings";
import { Button, Card } from "antd";
import { getSnippetByLanguage } from "../../Utils/language-snippets";
import codeService from "../../Services/code.service";
import { CodeBlock } from "react-code-blocks";
import Editor from "@monaco-editor/react";
import "./index.scss";

// Add this at the top of the file to fix TypeScript error for window.suppressProctoring
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
  interface Window {
    suppressProctoring?: boolean;
  }
}

interface CodeEditorProps {
  languages: ProgrammingLanguage[];
  setCompiling: (compiling: { loading: boolean; message: string }) => void;
  handleFullScreen: () => void;
  isFullScreen: boolean;
  onChange: (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    value: string | null
  ) => void;
  question: Question;
}

const PLACEHOLDER_TEXT = "Please select a programming language to continue.";

const judge0ToMonacoLanguageMap: { [key: string]: string } = {
  "Assembly (NASM 2.14.02)": "asm",
  "Bash (5.0.0)": "shell",
  "Basic (FBC 1.07.1)": "vb",
  "C (Clang 7.0.1)": "c",
  "C++ (Clang 7.0.1)": "cpp",
  "C (GCC 7.4.0)": "c",
  "C++ (GCC 7.4.0)": "cpp",
  "C (GCC 8.3.0)": "c",
  "C++ (GCC 8.3.0)": "cpp",
  "C (GCC 9.2.0)": "c",
  "C++ (GCC 9.2.0)": "cpp",
  "Clojure (1.10.1)": "clojure",
  "C# (Mono 6.6.0.161)": "csharp",
  "COBOL (GnuCOBOL 2.2)": "cobol",
  "Common Lisp (SBCL 2.0.0)": "lisp",
  "D (DMD 2.089.1)": "d",
  "Elixir (1.9.4)": "elixir",
  "Erlang (OTP 22.2)": "erlang",
  "F# (.NET Core SDK 3.1.202)": "fsharp",
  "Fortran (GFortran 9.2.0)": "fortran",
  "Go (1.13.5)": "go",
  "Groovy (3.0.3)": "groovy",
  "Haskell (GHC 8.8.1)": "haskell",
  "Java (OpenJDK 13.0.1)": "java",
  "JavaScript (Node.js 12.14.0)": "javascript",
  "Kotlin (1.3.70)": "kotlin",
  "Lua (5.3.5)": "lua",
  "Objective-C (Clang 7.0.1)": "objective-c",
  "OCaml (4.09.0)": "ocaml",
  "Octave (5.1.0)": "octave",
  "Pascal (FPC 3.0.4)": "pascal",
  "Perl (5.28.1)": "perl",
  "PHP (7.4.1)": "php",
  "Plain Text": "plaintext",
  "Prolog (GNU Prolog 1.4.5)": "prolog",
  "Python (2.7.17)": "python",
  "Python (3.8.1)": "python",
  "R (4.0.0)": "r",
  "Ruby (2.7.0)": "ruby",
  "Rust (1.40.0)": "rust",
  "Scala (2.13.2)": "scala",
  "SQL (SQLite 3.27.2)": "sql",
  "Swift (5.2.3)": "swift",
  "TypeScript (3.7.4)": "typescript",
  "Visual Basic.Net (vbnc 0.0.0.5943)": "vb",
  "Multi-file program": "plaintext",
  Executable: "plaintext",
};

function getMonacoLanguage(judge0LangName: string) {
  return judge0ToMonacoLanguageMap[judge0LangName] || "plaintext";
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  languages,
  setCompiling,
  handleFullScreen,
  isFullScreen,
  onChange,
  question,
}) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<ProgrammingLanguage | null>(null);
  const [code, setCode] = useState("");
  const [output, setOutput] = useState<string | null>(null);
  const editorRef = useRef<any>(null);
  const programmaticChange = useRef(false);
  const [isLanguageCleared, setIsLanguageCleared] = useState(false);

  useEffect(() => {
    if (question) {
      setSelectedLanguage(null);
      setOutput(null);
      setCode("");
    }
  }, [question]);

  const handleSelectLanguage = (language: ProgrammingLanguage | null) => {
    if (!language) {
      // Mobile-specific workaround: delay clearing language/readOnly
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        window.suppressProctoring = true;
        setTimeout(() => {
          window.suppressProctoring = false;
        }, 600);
      }
      setSelectedLanguage(null);
      setCode("");
      programmaticChange.current = true;
      onChange({} as any, null);
      if (isMobile) {
        setTimeout(() => setIsLanguageCleared(false), 300);
      } else {
        setIsLanguageCleared(false);
      }
      return;
    }
    setSelectedLanguage(language);
    const snippet = getSnippetByLanguage(language.name);
    setCode(snippet);
    programmaticChange.current = true;
    setIsLanguageCleared(false);
  };

  const onMonacoChange = (value: string | undefined) => {
    const safeValue = value ?? "";
    if (programmaticChange.current) {
      programmaticChange.current = false;
      setCode(safeValue);
      return;
    }
    setCode(safeValue);
    onChange({} as any, safeValue);
  };

  const handleRunCodeClick = async () => {
    if (!selectedLanguage) return;

    setCompiling({ loading: true, message: "Compiling..." });
    try {
      const output = await codeService.compileCode(code, selectedLanguage?.id);
      if (output) {
        console.log("🚀 ~ handleRunCodeClick ~ output:", output);
        setOutput(output);
      } else {
        setOutput("Error occurred while running the code.");
      }
    } catch (error: any) {
      console.log("🚀 ~ handleRunCode ~ error:", error);
    } finally {
      setCompiling({ loading: false, message: "" });
    }
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    setTimeout(() => {
      editor.layout();
    }, 100);
    editorRef.current = editor;
  };

  const handleEditorWillMount = (monaco: any) => {
    console.log("handleEditorWillMount:", monaco);
  };

  const handleEditorValidation = (markers: any) => {
    console.log("handleEditorValidation:", markers);
  };

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;
    const handleResize = () => {
      if (editorRef.current) {
        editorRef.current.layout();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return (
    <Card bordered={true} style={{ height: "100%", borderColor: "#000000" }}>
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: "0 0 50px" }}>
          <Settings
            languages={languages}
            handleSelectLanguage={handleSelectLanguage}
            selectedLanguage={selectedLanguage}
            handleFullScreen={handleFullScreen}
            isFullScreen={isFullScreen}
          />
        </div>

        <div className="code-editor-container" style={{ position: "relative" }}>
          <Editor
            theme="vs-dark"
            value={code}
            onChange={onMonacoChange}
            language={
              isLanguageCleared && window.innerWidth <= 768
                ? "plaintext"
                : selectedLanguage
                ? getMonacoLanguage(selectedLanguage.name)
                : undefined
            }
            className="code-editor"
            options={{
              readOnly: window.innerWidth <= 768 ? false : !selectedLanguage,
              minimap: { enabled: false },
              fontSize: 14,
              fontFamily: "monospace",
              scrollBeyondLastLine: false,
              wordWrap: "on",
              placeholder: PLACEHOLDER_TEXT,
              automaticLayout: true,
              accessibilitySupport: "off",
            }}
            onMount={handleEditorDidMount}
            beforeMount={handleEditorWillMount}
            onValidate={handleEditorValidation}

          />

          <div className="output-container">
            <h3 className="output-title-md" style={{ color: "#000000" }}>
              Output
            </h3>
            <CodeBlock
              customStyle={{
                padding: "0px",
                margin: "0px",
                height: "100%",
                width: "100%",
                background: "none",
              }}
              text={output ? output : "Run the code to see the output"}
              language="javascript"
              showLineNumbers={false}
            />
          </div>
        </div>

        <div style={{ marginTop: "10px" }}>
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#56AA30",
              paddingInline: "50px",
            }}
            onClick={handleRunCodeClick}
            disabled={!selectedLanguage}
          >
            Run
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CodeEditor;
